import { UserStorageKey } from "./userStorageKey";
import {
  readFromGlobalStorage,
  writeToGlobalStorage,
} from "./globalUserStorage";
import {
  Session,
  SessionState,
  useSession,
} from "fe-web-components/userSession/userSession";
import { useCallback, useContext, useMemo } from "react";
//region [[ Props ]]

/***
 * Does that key is a global key across the website?
 * (like user-agreement, sesstionId, or Speciality) or is it a
 * session-state data like patient case values or state
 */
export enum UserStorageKeyType {
  Session,
  Global,
}
export interface UseUserStorageApi {
  writeToStorage: (
    key: UserStorageKey,
    value: string,
    type?: UserStorageKeyType,
  ) => void;
  readFromStorage: <T>(key: UserStorageKey, otherSession?: Session) => T;
}

//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[ Functions ]]

/***
 * Author: Avi Gurfinkel (avi@kahun.com), May 12th. 2021
 * ----------------------------------------------------------------------------
 *
 * This hook is used to write/read from local storage.
 * since every tab stores it's state and case based on the local storage we
 * would like to wrap all writings an readings from the local storage using a
 * unified hook, this would allow us separation and will make sure every time
 * data is read or being written to the storage it doesn't override other tab
 * data
 *
 *              *       *       *       *       *       *       *
 *
 * currently (May 21st) we don't support working on couple of sessions
 * concurrently so every time contention occurs we choose the lates session which
 * had been saved in the storage. while making the older session invalid.
 *
 * In the future we may support couple of concurrent open tabs by simply storing
 * a machine key for the entire app storage
 */
export const useUserStorage = () => {
  const { session, updateSession } = useSession();

  const sessionIsValid = session?.isSessionValid;

  const writeToStorage = useCallback(
    (key: UserStorageKey, value: string) => {
      let updatedKey = key as string;
      if (session?.currentSession?.sessionId)
        updatedKey += `-${session?.currentSession?.sessionId}`;
      if (!session || sessionIsValid) {
        writeToGlobalStorage(updatedKey, value);
        // Support editor where no session is defined
        session && updateSession();
      } else {
        console.error("session invalid, no storage updates are possible");
      }
    },
    [sessionIsValid],
  );

  const readFromStorage = useCallback(
    (key: UserStorageKey, fromSession?: Session) => {
      let updatedKey = key as string;
      if (fromSession?.sessionId && fromSession?.sessionId !== "")
        // TODO: use funciton from useSession.tsx
        updatedKey += `-${fromSession?.sessionId}`;
      else if (session?.currentSession?.sessionId)
        // TODO: use funciton from useSession.tsx
        updatedKey += `-${session?.currentSession?.sessionId}`;
      let result = readFromGlobalStorage(updatedKey);

      let currentSessionId = fromSession?.basedOn || session?.basedOn;
      // Try to read from parent sessions
      while (currentSessionId && currentSessionId !== "" && !result) {
        // TODO: use funciton from useSession.tsx
        updatedKey = `${key}-${currentSessionId}`;
        result = readFromGlobalStorage(updatedKey);
        currentSessionId = (readFromGlobalStorage(
          `${UserStorageKey.Session}-${currentSessionId}`,
        ) as Session)?.basedOn;
      }
      // eventually return from pre-session based code
      return result || readFromGlobalStorage(key);
    },
    [],
  );

  return useMemo(
    () => ({ writeToStorage, readFromStorage } as UseUserStorageApi),
    [writeToStorage, readFromStorage],
  );
};
